/**
 * The guide's own interface text, per language. Pure data.
 *
 * In the repo rather than in Phrase (where the app's other UI text lives) on purpose: it ships
 * and is reviewed together with the guides it frames, the Node scripts and tests can read it,
 * and a missing Phrase key would blank the help panel rather than fall back.
 * Microcopy follows references/ui-spec.md of the user-guide-creator skill.
 */
import type { HelpLanguage } from './types';

export interface HelpStrings {
  // launcher + notices
  launcherTooltip: string;
  launcherAria: string;
  noticeDialogEmpty: string;
  noticeNothing: string;
  noticeNotOnScreenContext: string;
  noticeNotOnScreen: string;
  // drawer
  eyebrow: string;
  close: string;
  /** Header book button (app language) and the drawer's download button (guide language). */
  userManual: string;
  userManualDownload: string;
  tabPage: string;
  tabAsk: string;
  showMeAround: string;
  pointAtField: string;
  hint: string;
  searchAria: string;
  searchPlaceholder: string;
  matchLine: string;
  noMatch: string;
  notOnScreen: string;
  locate: string;
  locateAria: string;
  tourFrom: string;
  tourFromAria: string;
  tipsLabel: string;
  more: string;
  untranslated: string;
  // tour
  stepOf: string;
  prevSection: string;
  nextSection: string;
  endTour: string;
  back: string;
  next: string;
  done: string;
  tourAria: string;
  // step jump
  jumpSearchAria: string;
  jumpPlaceholder: string;
  jumpEmpty: string;
  jumpFooter: string;
  // inspector
  inspectBanner: string;
  inspectEsc: string;
  inspectLeave: string;
  inspectCardAria: string;
  continueFromHere: string;
  inspectMiss: string;
  // ask
  askPlaceholder: string;
  askHint: string;
  askLanguage: string;
  askThinking: string;
  askTakeMeThere: string;
  askTakeMeThereOpen: string;
  askTakeMeToList: string;
  askShowMe: string;
  askOpenIt: string;
  askNeeds: string;
  askInEnglish: string;
  askDidYouMean: string;
  askHelpful: string;
  askThanks: string;
  askYes: string;
  askNo: string;
  askHelpedAria: string;
  askNotHelpedAria: string;
  askEmpty: string;
  askMissNotOnScreen: string;
  askMissBehindSurface: string;
  askOnRecordPage: string;
  askError: string;
}

export const HELP_STRINGS: Record<HelpLanguage, HelpStrings> = {
  en: {
    launcherTooltip: 'Page guide: {title}',
    launcherAria: 'Open the page guide for {title}',
    noticeDialogEmpty:
      'Nothing in this dialog is documented yet — close it to tour the page, or read the panel.',
    noticeNothing: 'Nothing to point at on this screen yet — the guide in the panel covers it all.',
    noticeNotOnScreenContext: '“{title}” is not on screen yet. {context}',
    noticeNotOnScreen: '“{title}” is not on screen right now, so the tour cannot open on it.',
    eyebrow: 'Page guide',
    close: 'Close page guide',
    userManual: 'User manual',
    userManualDownload: 'Download the user manual in English (PDF)',
    tabPage: 'This page',
    tabAsk: 'Ask',
    showMeAround: 'Show me around',
    pointAtField: 'Point at a field',
    hint: 'The tour visits what is on screen right now — or only the open dialog, when there is one; its step counter jumps to any of them. “Point at a field” answers the other way round: click the control you are looking at and read what it does.',
    searchAria: 'Search this page’s guide',
    searchPlaceholder: 'Search all {n} entries…',
    matchLine: '{n} of {m} entries match “{q}”.',
    noMatch: 'Nothing on this page matches. The “Ask” tab searches the whole app.',
    notOnScreen: 'Not on screen right now.',
    locate: 'Show me where this is',
    locateAria: 'Locate: {title}',
    tourFrom: 'Walk me through from here',
    tourFromAria: 'Start the tour at: {title}',
    tipsLabel: 'Good to know',
    more: 'More',
    untranslated: 'Not translated yet — shown in English',
    stepOf: 'Step {n} of {m}',
    prevSection: 'Previous section',
    nextSection: 'Next section',
    endTour: 'End tour',
    back: 'Back',
    next: 'Next',
    done: 'Done',
    tourAria: 'Tour step {n} of {m}: {title}',
    jumpSearchAria: 'Search the steps on this page',
    jumpPlaceholder: 'Search this page’s guide…',
    jumpEmpty:
      'Nothing on this screen matches. The panel’s “This page” tab lists the parts that are not on screen right now too.',
    jumpFooter: '{n} of {m} · Enter goes to the first',
    inspectBanner: 'Click any field to read what it does',
    inspectEsc: 'Esc to leave',
    inspectLeave: 'Leave point-at-a-field mode',
    inspectCardAria: 'What this does: {title}',
    continueFromHere: 'Continue from here',
    inspectMiss:
      'No guide entry for this control yet — try the field itself, or the panel’s “This page” tab.',
    askPlaceholder: 'Ask how to do something…',
    askHint: 'Plain words work best — "import a bank statement", "send a reminder".',
    askLanguage: 'Guide language',
    askThinking: 'Looking…',
    askTakeMeThere: 'Take me there',
    askTakeMeThereOpen: 'Take me there and open it',
    askTakeMeToList: 'Take me to the list',
    askShowMe: 'Show me the control',
    askOpenIt: 'Open it',
    askNeeds: 'Needs',
    askInEnglish: 'shown in English',
    askDidYouMean: 'Try one of these:',
    askHelpful: 'Did this help?',
    askThanks: 'Thanks — noted.',
    askYes: 'Yes',
    askNo: 'No',
    askHelpedAria: 'Answer helped',
    askNotHelpedAria: 'Answer did not help',
    askEmpty: 'Ask a question about any screen in PCT.',
    askMissNotOnScreen: 'that control is not on screen right now.',
    askMissBehindSurface: 'that control is inside what the highlighted button opens.',
    askOnRecordPage: 'This control appears once you select a row in the list — select one first.',
    askError: 'The answer could not be computed. Please try again.',
  },
  lt: {
    launcherTooltip: 'Puslapio vadovas: {title}',
    launcherAria: 'Atidaryti puslapio „{title}“ vadovą',
    noticeDialogEmpty:
      'Šiame dialogo lange dar nieko neaprašyta — uždarykite jį, kad apžvelgtumėte puslapį, arba skaitykite skydelį.',
    noticeNothing: 'Šiame ekrane dar nėra ką parodyti — skydelio vadovas aprašo viską.',
    noticeNotOnScreenContext: '„{title}“ dar nematomas ekrane. {context}',
    noticeNotOnScreen:
      '„{title}“ šiuo metu nematomas ekrane, todėl ekskursijos nuo jo pradėti negalima.',
    eyebrow: 'Puslapio vadovas',
    close: 'Uždaryti puslapio vadovą',
    userManual: 'Naudotojo vadovas',
    userManualDownload: 'Atsisiųsti naudotojo vadovą lietuvių kalba (PDF)',
    tabPage: 'Šis puslapis',
    tabAsk: 'Klausti',
    showMeAround: 'Ekskursija po puslapį',
    pointAtField: 'Nurodyti lauką',
    hint: 'Ekskursija aplanko tai, kas dabar matoma ekrane, arba tik atidarytą dialogo langą, jei toks yra; jos žingsnių skaitiklis leidžia pereiti prie bet kurio žingsnio. „Nurodyti lauką“ veikia atvirkščiai: spustelėkite valdiklį, į kurį žiūrite, ir perskaitykite, ką jis daro.',
    searchAria: 'Ieškoti šio puslapio vadove',
    searchPlaceholder: 'Ieškoti tarp {n} įrašų…',
    matchLine: '{n} iš {m} įrašų atitinka „{q}“.',
    noMatch: 'Šiame puslapyje nieko nerasta. Skirtukas „Klausti“ ieško visoje programoje.',
    notOnScreen: 'Šiuo metu ekrane nematoma.',
    locate: 'Parodyk, kur tai yra',
    locateAria: 'Rasti: {title}',
    tourFrom: 'Vesk mane nuo čia',
    tourFromAria: 'Pradėti ekskursiją nuo: {title}',
    tipsLabel: 'Verta žinoti',
    more: 'Daugiau',
    untranslated: 'Dar neišversta – rodoma anglų kalba',
    stepOf: '{n} žingsnis iš {m}',
    prevSection: 'Ankstesnis skyrius',
    nextSection: 'Kitas skyrius',
    endTour: 'Baigti ekskursiją',
    back: 'Atgal',
    next: 'Toliau',
    done: 'Baigta',
    tourAria: 'Ekskursijos žingsnis {n} iš {m}: {title}',
    jumpSearchAria: 'Ieškoti šio puslapio žingsnių',
    jumpPlaceholder: 'Ieškoti šio puslapio vadove…',
    jumpEmpty:
      'Šiame ekrane nieko nerasta. Skydelio skirtuke „Šis puslapis“ išvardytos ir dalys, kurių dabar ekrane nėra.',
    jumpFooter: '{n} iš {m} · Enter pereina prie pirmo',
    inspectBanner: 'Spustelėkite bet kurį lauką ir perskaitykite, ką jis daro',
    inspectEsc: 'Esc – išeiti',
    inspectLeave: 'Išeiti iš lauko nurodymo režimo',
    inspectCardAria: 'Ką tai daro: {title}',
    continueFromHere: 'Tęsti nuo čia',
    inspectMiss:
      'Šiam valdikliui vadovo įrašo dar nėra — pabandykite patį lauką arba skydelio skirtuką „Šis puslapis“.',
    askPlaceholder: 'Paklauskite, kaip ką nors padaryti…',
    askHint: 'Geriausiai tinka paprasti žodžiai — „importuoti banko išrašą“, „išsiųsti priminimą“.',
    askLanguage: 'Vadovo kalba',
    askThinking: 'Ieškoma…',
    askTakeMeThere: 'Nuvesk mane ten',
    askTakeMeThereOpen: 'Nuvesk ir atidaryk',
    askTakeMeToList: 'Nuvesk į sąrašą',
    askShowMe: 'Parodyk valdiklį',
    askOpenIt: 'Atidaryk',
    askNeeds: 'Reikia',
    askInEnglish: 'rodoma anglų kalba',
    askDidYouMean: 'Pabandykite vieną iš šių:',
    askHelpful: 'Ar tai padėjo?',
    askThanks: 'Ačiū — užfiksuota.',
    askYes: 'Taip',
    askNo: 'Ne',
    askHelpedAria: 'Atsakymas padėjo',
    askNotHelpedAria: 'Atsakymas nepadėjo',
    askEmpty: 'Užduokite klausimą apie bet kurį PCT ekraną.',
    askMissNotOnScreen: 'šis valdiklis šiuo metu nematomas ekrane.',
    askMissBehindSurface: 'šis valdiklis yra tame, ką atidaro paryškintas mygtukas.',
    askOnRecordPage: 'Šis valdiklis atsiranda pasirinkus eilutę sąraše — pirmiausia pasirinkite ją.',
    askError: 'Atsakymo apskaičiuoti nepavyko. Bandykite dar kartą.',
  },
  lv: {
    launcherTooltip: 'Lapas ceļvedis: {title}',
    launcherAria: 'Atvērt lapas “{title}” ceļvedi',
    noticeDialogEmpty:
      'Šajā dialoglodziņā vēl nekas nav aprakstīts — aizveriet to, lai apskatītu lapu, vai lasiet paneli.',
    noticeNothing: 'Šajā ekrānā vēl nav ko parādīt — paneļa ceļvedis apraksta visu.',
    noticeNotOnScreenContext: '“{title}” vēl nav redzams ekrānā. {context}',
    noticeNotOnScreen: '“{title}” pašlaik nav redzams ekrānā, tāpēc ekskursiju no tā sākt nevar.',
    eyebrow: 'Lapas ceļvedis',
    close: 'Aizvērt lapas ceļvedi',
    userManual: 'Lietotāja rokasgrāmata',
    userManualDownload: 'Lejupielādēt lietotāja rokasgrāmatu latviešu valodā (PDF)',
    tabPage: 'Šī lapa',
    tabAsk: 'Jautāt',
    showMeAround: 'Ekskursija pa lapu',
    pointAtField: 'Norādīt lauku',
    hint: 'Ekskursija apmeklē to, kas pašlaik redzams ekrānā, vai tikai atvērto dialoglodziņu, ja tāds ir; tās soļu skaitītājs ļauj pāriet uz jebkuru soli. “Norādīt lauku” darbojas otrādi: noklikšķiniet uz vadīklas, kuru skatāties, un izlasiet, ko tā dara.',
    searchAria: 'Meklēt šīs lapas ceļvedī',
    searchPlaceholder: 'Meklēt starp {n} ierakstiem…',
    matchLine: '{n} no {m} ierakstiem atbilst “{q}”.',
    noMatch: 'Šajā lapā nekas neatbilst. Cilne “Jautāt” meklē visā lietotnē.',
    notOnScreen: 'Pašlaik nav redzams ekrānā.',
    locate: 'Parādīt, kur tas ir',
    locateAria: 'Atrast: {title}',
    tourFrom: 'Vadīt mani no šejienes',
    tourFromAria: 'Sākt ekskursiju no: {title}',
    tipsLabel: 'Noderīgi zināt',
    more: 'Vairāk',
    untranslated: 'Vēl nav iztulkots – rādīts angliski',
    stepOf: '{n}. solis no {m}',
    prevSection: 'Iepriekšējā sadaļa',
    nextSection: 'Nākamā sadaļa',
    endTour: 'Beigt ekskursiju',
    back: 'Atpakaļ',
    next: 'Tālāk',
    done: 'Gatavs',
    tourAria: 'Ekskursijas solis {n} no {m}: {title}',
    jumpSearchAria: 'Meklēt šīs lapas soļus',
    jumpPlaceholder: 'Meklēt šīs lapas ceļvedī…',
    jumpEmpty:
      'Šajā ekrānā nekas neatbilst. Paneļa cilnē “Šī lapa” ir uzskaitītas arī daļas, kas pašlaik nav redzamas ekrānā.',
    jumpFooter: '{n} no {m} · Enter pāriet uz pirmo',
    inspectBanner: 'Noklikšķiniet uz jebkura lauka, lai izlasītu, ko tas dara',
    inspectEsc: 'Esc – iziet',
    inspectLeave: 'Iziet no lauka norādīšanas režīma',
    inspectCardAria: 'Ko tas dara: {title}',
    continueFromHere: 'Turpināt no šejienes',
    inspectMiss:
      'Šai vadīklai vēl nav ceļveža ieraksta — mēģiniet pašu lauku vai paneļa cilni “Šī lapa”.',
    askPlaceholder: 'Jautājiet, kā kaut ko izdarīt…',
    askHint: 'Vislabāk der vienkārši vārdi — “importēt bankas izrakstu”, “nosūtīt atgādinājumu”.',
    askLanguage: 'Ceļveža valoda',
    askThinking: 'Meklē…',
    askTakeMeThere: 'Aizvest mani turp',
    askTakeMeThereOpen: 'Aizvest un atvērt',
    askTakeMeToList: 'Aizvest uz sarakstu',
    askShowMe: 'Parādīt vadīklu',
    askOpenIt: 'Atvērt',
    askNeeds: 'Nepieciešams',
    askInEnglish: 'rādīts angliski',
    askDidYouMean: 'Izmēģiniet kādu no šiem:',
    askHelpful: 'Vai tas palīdzēja?',
    askThanks: 'Paldies — atzīmēts.',
    askYes: 'Jā',
    askNo: 'Nē',
    askHelpedAria: 'Atbilde palīdzēja',
    askNotHelpedAria: 'Atbilde nepalīdzēja',
    askEmpty: 'Uzdodiet jautājumu par jebkuru PCT ekrānu.',
    askMissNotOnScreen: 'šī vadīkla pašlaik nav redzama ekrānā.',
    askMissBehindSurface: 'šī vadīkla atrodas tajā, ko atver izceltā poga.',
    askOnRecordPage: 'Šī vadīkla parādās, kad sarakstā izvēlaties rindu — vispirms izvēlieties to.',
    askError: 'Atbildi neizdevās aprēķināt. Lūdzu, mēģiniet vēlreiz.',
  },
};

export const LANGUAGE_LABELS: Record<HelpLanguage, string> = { en: 'EN', lt: 'LT', lv: 'LV' };

/** "{n} of {m}" + { n: 3, m: 9 } → "3 of 9". Unknown placeholders are left as they are. */
export const fmt = (template: string, vars: Record<string, string | number>): string =>
  template.replace(/\{(\w+)\}/g, (whole, key: string) => (key in vars ? String(vars[key]) : whole));
